import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { TriangleAlertIcon } from 'lucide-react';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import { Separator } from '@voll-intelligence/ui-kit/ui/separator';
import { Skeleton } from '@voll-intelligence/ui-kit/ui/skeleton';
import { useMediaQuery } from '@voll-intelligence/ui-kit/hooks/use-media-query';
import type { HotelDetailsView, HotelRoomView } from '../models.js';
import { HotelRooms, type HotelRoomFilter, type HotelRoomsCopy } from '../rooms/hotel_rooms.js';
import { HOTEL_NARROW_QUERY } from '../search/hotel_narrow.js';
import { BottomSheet } from '../../shared/bottom_sheet.js';
import { HotelDetailsAmenities } from './hotel_details_amenities.js';
import { HotelDetailsCancellation } from './hotel_details_cancellation.js';
import { HotelDetailsFavoriteButton, type HotelDetailsFavorite } from './hotel_details_favorite.js';
import { HotelDetailsGallery } from './hotel_details_gallery.js';
import { HotelDetailsAgreementBadge, HotelDetailsPageRating } from './hotel_details_identity.js';
import {
  HotelDetailsCloseButton,
  HotelDetailsNewTabButton,
  type HotelDetailsOpenInNewTab,
} from './hotel_details_header_button.js';
import { HotelDetailsLocation } from './hotel_details_location.js';
import { HotelDetailsReviews } from './hotel_details_reviews.js';
import {
  HotelDetailsSkeletonAmenities,
  HotelDetailsSkeletonBody,
  HotelDetailsSkeletonGallery,
  HotelDetailsSkeletonRooms,
} from './hotel_details_skeleton.js';
import { HotelDetailsTabs, type HotelDetailsTab } from './hotel_details_tabs.js';
import './hotel_details_panel.css';

export type HotelDetailsCopy = {
  closeLabel: string;
  previousPhotoLabel: string;
  nextPhotoLabel: string;
  overviewTabLabel: string;
  roomsTabLabel: string;
  aboutTabLabel: string;
  sectionsNavigationLabel: string;
  /** As setas da régua de seções quando as abas não cabem na largura. */
  previousTabsLabel: string;
  nextTabsLabel: string;
  readMoreLabel: string;
  readLessLabel: string;
  allAmenitiesLabel: string;
  /** O rótulo do MESMO botão com a lista aberta ("Menos comodidades") — sem
   *  ele o controle expandido seguiria convidando a "ver todas". */
  fewerAmenitiesLabel: string;
  reviewsTabLabel: string;
  locationTabLabel: string;
  cancellationTabLabel: string;
  loadingLabel: string;
  errorTitleLabel: string;
  retryLabel: string;
  /** O hotel carregou e não veio quarto nenhum. Sem esta frase a seção sumia
   *  calada, e a gaveta virava um beco: preço na lista, nenhuma tarifa aqui e
   *  nada dizendo por quê. */
  roomsEmptyLabel: string;
};

/** Cópia extra que só a `variant="page"` usa: o CTA que rola até os quartos e
 *  o selo da galeria. Fora da `HotelDetailsCopy` de propósito — a gaveta não tem
 *  "Ver quartos" (ela tem abas), então essas chaves não existem lá. A volta e a
 *  trilha da página moram na barra do HOST (#3989, #4923). Exportada (e
 *  re-exportada no `index.ts`): o `HotelDetailsPage` da genui-kit a recebe como
 *  prop, com os rótulos que o web-chat supre pela sua i18n. */
export type HotelDetailsPageCopy = {
  viewRoomsLabel: string;
  /** Rótulo do `N fotos` no canto da foto grande da galeria (ex.: "fotos"). */
  photosBadgeLabel: string;
};

/** O conteúdo do detalhe, comum às duas variantes (gaveta e página) — as seções
 *  reusam este contrato. Sem `state`/`message` (orquestração do painel) nem os
 *  campos da variante. Tipo LOCAL: base interna de `HotelDetailsPanelProps` (que
 *  é o export público); não tem consumidor externo, então exportá-lo dispararia o
 *  knip ratchet de tipo ocioso. */
type HotelDetailsContentProps = {
  copy: HotelDetailsCopy;
  roomsCopy: HotelRoomsCopy;
  hotel?: HotelDetailsView;
  rooms?: readonly HotelRoomView[];
  /** O porão (`hotelRooms.otherOptions`): quartos com nome ilegível, colapsados
   *  numa seção própria dentro do `<HotelRooms>` da aba de quartos. Ausente
   *  quando não há nenhum — nunca um array vazio (mesmo contrato do adapter). */
  otherRooms?: readonly HotelRoomView[];
  selectedRateId?: string;
  /** Os chips com que a lista de quartos abre ligados — o recorte da lista de
   *  hotéis de onde o hotel veio (#5820). Só o estado inicial do `<HotelRooms>`. */
  initialFilters?: readonly HotelRoomFilter[];
  readOnly?: boolean;
  busy?: boolean;
  favorite?: HotelDetailsFavorite;
  /** O ↗ à direita do coração (#5893), na página e na folha. Ausente ⇒ sem ↗. */
  openInNewTab?: HotelDetailsOpenInNewTab;
  /** A fileira da estadia (`Quem`·`Quando` + o alerta de datas), que na página
   *  mora DENTRO da seção de quartos, entre o título e a lista (designer,
   *  2026-08-24): solta entre as seções ela leria como uma faixa de busca.
   *  Presente, o título da seção vira uma linha SÓLIDA daqui e a lista abre mão
   *  do próprio cabeçalho. */
  stayArea?: ReactNode;
  /** O que a seção de quartos mostra no lugar da lista:
   *  - `no-dates`: estadia ainda sem período confirmado — a MOLDURA estática a
   *    40% (nunca esqueleto: esqueleto promete carga, e nada está carregando);
   *  - `loading`: período confirmado invalidou a lista — esqueleto animado;
   *  - `ready` (default): a lista de sempre. */
  roomsPhase?: 'no-dates' | 'loading' | 'empty' | 'ready';
  /** Fecha a gaveta (drawer) OU volta pra lista (page) — a porta de saída. */
  onClose: () => void;
  onRetry?: () => void;
  onRateSelect: (roomId: string, rateId: string) => void;
  onRoomDetailsOpen: (roomId: string) => void;
};

/**
 * `drawer` (padrão) = gaveta lateral com abas/✕; `page` = página inteira (galeria
 * full-width, coluna 896px, sem abas, trilha no topo) — o "abrir o hotel como
 * nova aba" do fluxo determinístico. No palco ESTREITO (`HOTEL_NARROW_QUERY`) a
 * página vira a FOLHA de tela cheia (`HotelDetailsSheet`, #2668): sem trilha,
 * com ✕ e abas, porque no celular ninguém foi pra outra aba.
 *
 * Tipo PLANO (não união discriminada) de propósito: a união faz o `StoryObj` do
 * Storybook colapsar os args para `never` e o `Partial<>` dos helpers de teste
 * perder `pageCopy`. `pageCopy` é obrigatória na prática quando `variant="page"`
 * — o switch cai na gaveta sem ela —, e o único consumidor (o web-chat, ao
 * reconstruir `?hotel=`) sempre a passa; a story e os testes exercitam o caminho.
 */
export type HotelDetailsPanelProps = HotelDetailsContentProps & {
  /** O painel só existe depois que um hotel foi escolhido, então não há `idle`. */
  state: 'loading' | 'ready' | 'error';
  message?: string;
  variant?: 'drawer' | 'page';
  /** Obrigatória quando `variant="page"`. */
  pageCopy?: HotelDetailsPageCopy;
};

function detailTabs({
  hotel,
  copy,
}: Pick<HotelDetailsPanelProps, 'hotel' | 'copy'>): HotelDetailsTab[] {
  if (!hotel) return [];
  return [
    ...(hotel.amenitySections.length > 0 ? [{ id: 'overview', label: copy.overviewTabLabel }] : []),
    // A seção de quartos existe SEMPRE que o hotel carregou — com a lista ou com
    // a frase de "sem quarto" —, então a régua sempre tem para onde levar.
    { id: 'rooms', label: copy.roomsTabLabel },
    ...(hotel.description ? [{ id: 'about', label: copy.aboutTabLabel }] : []),
    ...(hotel.cancellation ? [{ id: 'cancellation', label: copy.cancellationTabLabel }] : []),
    ...(hotel.location ? [{ id: 'location', label: copy.locationTabLabel }] : []),
    ...(hotel.reviews.length > 0 ? [{ id: 'reviews', label: copy.reviewsTabLabel }] : []),
  ];
}

function HotelDetailsSection({
  id,
  label,
  children,
}: {
  readonly id: string;
  readonly label: string;
  readonly children: ReactNode;
}) {
  return (
    <section id={id} aria-label={label} data-hotel-details-section>
      {children}
    </section>
  );
}

function HotelDetailsOverview({
  hotel,
  overviewLabel,
  allAmenitiesLabel,
  fewerAmenitiesLabel,
}: {
  readonly hotel: HotelDetailsView;
  readonly overviewLabel: string;
  readonly allAmenitiesLabel: string;
  readonly fewerAmenitiesLabel: string;
}) {
  // Sem comodidades a seção ficaria vazia: some, com a aba e a divisória (#6203).
  return hotel.amenitySections.length === 0 ? null : (
    <HotelDetailsSection id="overview" label={overviewLabel}>
      <div className="hui-hotel-details-overview">
        <HotelDetailsAmenities
          sections={hotel.amenitySections}
          allAmenitiesLabel={allAmenitiesLabel}
          fewerAmenitiesLabel={fewerAmenitiesLabel}
        />
      </div>
    </HotelDetailsSection>
  );
}

function HotelDetailsAboutSection({
  hotel,
  copy,
}: {
  readonly hotel: HotelDetailsView;
  readonly copy: HotelDetailsCopy;
}) {
  const [expanded, setExpanded] = useState(false);
  if (!hotel.description) return null;
  return (
    <HotelDetailsSection id="about" label={copy.aboutTabLabel}>
      <section className="hui-hotel-details-about">
        <h3>{copy.aboutTabLabel}</h3>
        <p data-expanded={expanded}>{hotel.description}</p>
        <button
          type="button"
          aria-expanded={expanded}
          onClick={() => {
            setExpanded((current) => !current);
          }}
        >
          {expanded ? copy.readLessLabel : copy.readMoreLabel}
        </button>
      </section>
    </HotelDetailsSection>
  );
}

function HotelDetailsReviewsSection({
  hotel,
  copy,
}: Pick<HotelDetailsPanelProps, 'hotel' | 'copy'> & {
  readonly hotel: HotelDetailsView;
}) {
  if (hotel.reviews.length === 0) return null;
  return (
    <HotelDetailsSection id="reviews" label={copy.reviewsTabLabel}>
      <HotelDetailsReviews label={copy.reviewsTabLabel} reviews={hotel.reviews} />
    </HotelDetailsSection>
  );
}

function HotelDetailsCancellationSection({
  hotel,
  copy,
}: Pick<HotelDetailsPanelProps, 'hotel' | 'copy'> & {
  readonly hotel: HotelDetailsView;
}) {
  if (!hotel.cancellation) return null;
  return (
    <HotelDetailsSection id="cancellation" label={copy.cancellationTabLabel}>
      <HotelDetailsCancellation
        heading={copy.cancellationTabLabel}
        cancellation={hotel.cancellation}
      />
    </HotelDetailsSection>
  );
}

function HotelDetailsLocationSection({
  hotel,
  copy,
}: Pick<HotelDetailsPanelProps, 'hotel' | 'copy'> & {
  readonly hotel: HotelDetailsView;
}) {
  /* A coordenada é o que faz a seção existir — o mapa é o assunto. O endereço é
   *  legenda: sem ele a seção continua entregando mapa e distância. */
  if (!hotel.location) return null;
  return (
    <HotelDetailsSection id="location" label={copy.locationTabLabel}>
      <HotelDetailsLocation
        heading={copy.locationTabLabel}
        label={hotel.locationLabel}
        map={hotel.locationMap}
        distanceLabel={hotel.distanceLabel}
        travelTimeLabel={hotel.travelTimeLabel}
      />
    </HotelDetailsSection>
  );
}

/** O card de quarto em esqueleto — a MESMA anatomia do card real: foto à
 *  esquerda, linhas de nome/specs, e o trilho de preço embaixo do `border-t`. */
function HotelRoomCardSkeleton() {
  return (
    <div className="ring-foreground/10 overflow-hidden rounded-xl ring-1">
      <div className="flex items-stretch">
        <Skeleton className="h-28 w-32 shrink-0 rounded-none" />
        <div className="grid flex-1 content-start gap-2 p-3">
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-3.5 w-1/2" />
          <Skeleton className="h-3.5 w-1/3" />
        </div>
      </div>
      <div className="border-border flex items-center justify-between border-t p-3">
        <Skeleton className="h-3.5 w-24" />
        <Skeleton className="h-8 w-24 rounded-lg" />
      </div>
    </div>
  );
}

/** O lugar dos quartos ANTES de haver datas (decisão do designer, 2026-08-24) —
 *  e ele NÃO é esqueleto: esqueleto promete carga, e aqui nada está carregando;
 *  a pilha inteira pulsando por tempo indeterminado só cansa. O que fica é a
 *  MOLDURA discreta do que virá — dois chips no lugar dos filtros e um card
 *  fantasma —, estática e a 40%: contexto do que as datas destravam. */
function HotelRoomsNoDatesPlaceholder() {
  return (
    <div
      aria-hidden="true"
      data-rooms-placeholder
      className="grid gap-4 opacity-40 [&_[data-slot=skeleton]]:animate-none"
    >
      <div className="flex gap-2">
        <Skeleton className="h-6 w-20 rounded-full" />
        <Skeleton className="h-6 w-24 rounded-full" />
      </div>
      <HotelRoomCardSkeleton />
    </div>
  );
}

/** A lista em recarga (período novo confirmado): chips + dois cards em
 *  esqueleto ANIMADO — aqui a carga é real e o pulso é a promessa certa. Sem
 *  barra de título: com a fileira da estadia em cena o título é sólido acima. */
function HotelRoomsLoadingSkeleton() {
  return (
    <div aria-hidden="true" data-rooms-loading className="grid gap-4">
      <div className="flex gap-2">
        <Skeleton className="h-7 w-28 rounded-full" />
        <Skeleton className="h-7 w-36 rounded-full" />
      </div>
      <HotelRoomCardSkeleton />
      <HotelRoomCardSkeleton />
    </div>
  );
}

/** O corpo da seção de quartos numa fase só — extraído da seção pelo par
 *  estadia×fase (o ternário aninhado no JSX estourava a leitura). */
function HotelRoomsSectionBody({
  rooms,
  otherRooms,
  roomsCopy,
  emptyLabel,
  selectedRateId,
  initialFilters,
  readOnly,
  busy,
  roomsPhase,
  hideListHeader,
  onRateSelect,
  onRoomDetailsOpen,
}: Omit<HotelDetailsContentProps, 'copy' | 'hotel' | 'onClose' | 'onRetry' | 'stayArea'> & {
  readonly emptyLabel: string;
  readonly hideListHeader: boolean;
}) {
  if (roomsPhase === 'no-dates') return <HotelRoomsNoDatesPlaceholder />;
  if (roomsPhase === 'loading') {
    // `aria-busy` no contêiner (o esqueleto em si é decorativo, `aria-hidden`):
    // sem ele o leitor de tela via a lista SUMIR sem substituto nem estado de
    // carga — o `aria-busy` da raiz do painel só reflete `busy`, e a recarga
    // da estadia viaja por `roomsPhase` (achado do CodeRabbit, PR #2258).
    return (
      <div aria-busy="true">
        <HotelRoomsLoadingSkeleton />
      </div>
    );
  }
  // `empty` FORÇADO pelo host: a re-estadia respondeu "sem disponibilidade na
  // janela" — a lista velha mentiria o preço das datas novas, então sai de
  // cena e fica a mesma frase do hotel sem tarifa.
  if (roomsPhase === 'empty') return <p className="hui-hotel-details-rooms-empty">{emptyLabel}</p>;
  // Sem quarto a seção FICA, com a frase no lugar da lista: o hotel carregou
  // (esta subárvore só monta com `hotel`), então "nenhuma tarifa" é resposta, e
  // sumir com a seção deixava a superfície sem explicação nenhuma.
  if (!rooms || rooms.length === 0) {
    return <p className="hui-hotel-details-rooms-empty">{emptyLabel}</p>;
  }
  return (
    <HotelRooms
      rooms={rooms}
      otherRooms={otherRooms}
      copy={roomsCopy}
      selectedRateId={selectedRateId}
      initialFilters={initialFilters}
      readOnly={readOnly}
      busy={busy}
      hideHeader={hideListHeader}
      onRateSelect={onRateSelect}
      onRoomDetailsOpen={onRoomDetailsOpen}
    />
  );
}

function HotelDetailsRoomsSection({
  sectionLabel,
  stayArea,
  roomsPhase = 'ready',
  ...bodyProps
}: Omit<HotelDetailsContentProps, 'copy' | 'hotel' | 'onClose' | 'onRetry'> & {
  readonly sectionLabel: string;
  readonly emptyLabel: string;
}) {
  const body = (
    <HotelRoomsSectionBody
      {...bodyProps}
      roomsPhase={roomsPhase}
      hideListHeader={stayArea != null}
    />
  );
  if (stayArea == null) {
    return (
      <HotelDetailsSection id="rooms" label={sectionLabel}>
        {body}
      </HotelDetailsSection>
    );
  }
  return (
    /* **Os campos moram DENTRO da seção**, sob um título que fica SÓLIDO em
       todas as fases (designer, 2026-08-24): título, depois `Quem`·`Quando`,
       depois a lista — a mesma linha de título que a lista desenharia, então a
       lista renderiza sem o próprio cabeçalho. */
    <HotelDetailsSection id="rooms" label={sectionLabel}>
      <div className="grid gap-4">
        {/* `text-ui font-semibold` (14/20), NÃO o text-heading da lista: com os
            campos da estadia em cena o único heading grande da página é o nome
            do hotel — é a régua do `CabecalhoDosQuartos` do protótipo. */}
        <div className="flex min-h-8 items-center">
          <h2 className="text-ui shrink-0 font-semibold whitespace-nowrap">
            {bodyProps.roomsCopy.title}
          </h2>
        </div>
        {stayArea}
        {body}
      </div>
    </HotelDetailsSection>
  );
}

function HotelDetailsSections({
  hotel,
  copy,
  ...roomsProps
}: Omit<HotelDetailsContentProps, 'onClose' | 'onRetry'> & {
  readonly hotel: HotelDetailsView;
}) {
  return (
    <>
      <HotelDetailsOverview
        hotel={hotel}
        overviewLabel={copy.overviewTabLabel}
        allAmenitiesLabel={copy.allAmenitiesLabel}
        fewerAmenitiesLabel={copy.fewerAmenitiesLabel}
      />
      {hotel.amenitySections.length > 0 && <Separator />}
      <HotelDetailsRoomsSection
        {...roomsProps}
        sectionLabel={copy.roomsTabLabel}
        emptyLabel={copy.roomsEmptyLabel}
      />
      {hotel.description && <Separator />}
      <HotelDetailsAboutSection hotel={hotel} copy={copy} />
      {/* Logo abaixo dos quartos e do "sobre": quem acabou de ler as tarifas é
          exatamente quem quer saber até quando dá para desistir delas. */}
      {hotel.cancellation && <Separator />}
      <HotelDetailsCancellationSection hotel={hotel} copy={copy} />
      {hotel.location && <Separator />}
      <HotelDetailsLocationSection hotel={hotel} copy={copy} />
      {hotel.reviews.length > 0 && <Separator />}
      <HotelDetailsReviewsSection hotel={hotel} copy={copy} />
    </>
  );
}

function scrollSectionIntoView(section: HTMLElement) {
  const scrollIntoView: unknown = Reflect.get(section, 'scrollIntoView');
  if (typeof scrollIntoView !== 'function') return;
  Reflect.apply(scrollIntoView, section, [{ block: 'start', behavior: 'smooth' }]);
}

/** As abas × o corpo que rola: a aba ativa segue a seção sob a barra
 *  (`trackSection`, no `onScroll` do corpo) e um toque na aba rola até a seção
 *  (`activateSection`). Um hook só porque a gaveta e a folha do celular
 *  compartilham a régua, e só a moldura em volta muda. */
function useHotelDetailsScrollspy(tabs: readonly HotelDetailsTab[]) {
  const [activeTab, setActiveTab] = useState(tabs[0]?.id ?? 'overview');
  const bodyRef = useRef<HTMLDivElement>(null);
  const visibleTab = tabs.some((tab) => tab.id === activeTab)
    ? activeTab
    : (tabs[0]?.id ?? 'overview');

  // A tab that left the list gives way to the first one (during render).
  if (activeTab !== visibleTab) setActiveTab(visibleTab);

  const activateSection = useCallback((id: string) => {
    setActiveTab(id);
    const body = bodyRef.current;
    const section = body?.querySelector<HTMLElement>(`#${id}`);
    if (!body || !section) return;
    scrollSectionIntoView(section);
  }, []);

  const trackSection = useCallback(
    (body: HTMLDivElement) => {
      // Em RETÂNGULOS, não em `offsetTop` (mesma razão do `trackIdentity` da
      // folha): o corpo não é posicionado, então o `offsetParent` das seções é
      // o popup fixo do Drawer e o `offsetTop` carrega cabeçalho/barra +
      // safe-area. A seção que a aba acabou de rolar parava ACIMA do limiar e a
      // anterior ficava destacada (#3845). O limiar é o `scroll-margin-top`
      // das seções (3rem) mais uma folga.
      const bodyTop = body.getBoundingClientRect().top;
      const active = tabs
        .map((tab) => ({ tab, section: body.querySelector<HTMLElement>(`#${tab.id}`) }))
        .filter((entry): entry is { tab: HotelDetailsTab; section: HTMLElement } =>
          Boolean(entry.section),
        )
        .filter(({ section }) => section.getBoundingClientRect().top - bodyTop <= 56)
        .at(-1)?.tab;
      if (active) setActiveTab(active.id);
    },
    [tabs],
  );

  return { bodyRef, visibleTab, activateSection, trackSection };
}

function HotelDetailsReady({
  copy,
  roomsCopy,
  rooms,
  otherRooms,
  selectedRateId,
  initialFilters,
  readOnly = false,
  busy = false,
  favorite,
  stayArea,
  roomsPhase,
  onClose,
  onRateSelect,
  onRoomDetailsOpen,
  hotel,
}: Omit<HotelDetailsContentProps, 'onRetry'> & { readonly hotel: HotelDetailsView }) {
  const tabs = useMemo(() => detailTabs({ hotel, copy }), [hotel, copy]);
  const { bodyRef, visibleTab, activateSection, trackSection } = useHotelDetailsScrollspy(tabs);

  return (
    <section
      className="genui2 hui-hotel-details"
      aria-label={hotel.name}
      aria-busy={busy || undefined}
    >
      <header className="hui-hotel-details-header">
        <div className="hui-hotel-details-identity">
          <HotelDetailsAgreementBadge label={hotel.agreedRateLabel} />
          <h2>{hotel.name}</h2>
          {hotel.rating && (
            <p className="hui-hotel-details-rating">
              {hotel.rating.label}
              {hotel.rating.value === undefined ? '' : `: ${String(hotel.rating.value)}`}
            </p>
          )}
        </div>
        <span className="hui-hotel-details-actions">
          {favorite && <HotelDetailsFavoriteButton favorite={favorite} disabled={busy} />}
          <HotelDetailsCloseButton label={copy.closeLabel} onClose={onClose} />
        </span>
      </header>
      <div
        ref={bodyRef}
        className="hui-hotel-details-body scrollbar-rail"
        onScroll={(event) => {
          trackSection(event.currentTarget);
        }}
      >
        <HotelDetailsGallery
          photos={hotel.photos}
          title={hotel.name}
          copy={{
            closeLabel: copy.closeLabel,
            previousPhotoLabel: copy.previousPhotoLabel,
            nextPhotoLabel: copy.nextPhotoLabel,
          }}
        />
        <HotelDetailsTabs
          tabs={tabs}
          activeId={visibleTab}
          copy={copy}
          onActiveChange={activateSection}
        />
        <HotelDetailsSections
          hotel={hotel}
          copy={copy}
          rooms={rooms}
          otherRooms={otherRooms}
          roomsCopy={roomsCopy}
          selectedRateId={selectedRateId}
          initialFilters={initialFilters}
          readOnly={readOnly}
          busy={busy}
          stayArea={stayArea}
          roomsPhase={roomsPhase}
          onRateSelect={onRateSelect}
          onRoomDetailsOpen={onRoomDetailsOpen}
        />
      </div>
    </section>
  );
}

/** A raiz da Página do Hotel é quem rola: a barra é a única do DS. */
const HOTEL_PAGE_CLASS = 'genui2 hui-hotel-page scrollbar-rail';

/**
 * A "Página do Hotel" — o hotel em tela cheia, o "abrir como nova aba" do fluxo
 * determinístico. Nada aqui é peça nova: reusa as MESMAS seções da gaveta
 * (`HotelDetailsSections`) e a mesma galeria/lightbox; a página só troca a
 * geometria — galeria em largura inteira, coluna central de leitura, e a trilha
 * de volta no topo. SEM abas/scrollspy (a trilha já é a navegação) e SEM ✕ (o
 * ← e o 1º segmento voltam pra lista). Difere do template do design em um ponto:
 * lá a trilha mora na topbar da subtela (slot que o web-chat não tem), então
 * aqui ela vive in-content, no topo da página.
 */
function HotelDetailsPageReady({
  copy,
  pageCopy,
  roomsCopy,
  rooms,
  otherRooms,
  selectedRateId,
  initialFilters,
  readOnly = false,
  busy = false,
  favorite,
  openInNewTab,
  stayArea,
  roomsPhase,
  onRateSelect,
  onRoomDetailsOpen,
  hotel,
}: Omit<HotelDetailsContentProps, 'onClose' | 'onRetry'> & {
  readonly hotel: HotelDetailsView;
  readonly pageCopy: HotelDetailsPageCopy;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  // "Ver quartos" rola até a seção de quartos (id `rooms`, o mesmo do scrollspy
  // da gaveta) — a única âncora que a página oferece.
  const viewRooms = useCallback(() => {
    const section = rootRef.current?.querySelector<HTMLElement>('#rooms');
    if (section) scrollSectionIntoView(section);
  }, []);
  return (
    <div ref={rootRef} className={HOTEL_PAGE_CLASS} aria-busy={busy || undefined}>
      <HotelDetailsGallery
        photos={hotel.photos}
        title={hotel.name}
        variant="page"
        photosBadgeLabel={pageCopy.photosBadgeLabel}
        copy={{
          closeLabel: copy.closeLabel,
          previousPhotoLabel: copy.previousPhotoLabel,
          nextPhotoLabel: copy.nextPhotoLabel,
        }}
      />
      <div className="hui-hotel-page-column">
        <header className="hui-hotel-page-identity">
          <div className="hui-hotel-page-identity-text">
            <HotelDetailsAgreementBadge label={hotel.agreedRateLabel} />
            {hotel.rating && <HotelDetailsPageRating rating={hotel.rating} />}
            <h2>{hotel.name}</h2>
            {hotel.locationLabel && <p className="hui-hotel-page-address">{hotel.locationLabel}</p>}
          </div>
          <span className="hui-hotel-page-identity-actions">
            {favorite && (
              <HotelDetailsFavoriteButton favorite={favorite} disabled={busy} size="icon-lg" />
            )}
            {openInNewTab && (
              <HotelDetailsNewTabButton openInNewTab={openInNewTab} size="icon-lg" />
            )}
            <Button size="lg" className="hui-hotel-page-cta" onClick={viewRooms}>
              {pageCopy.viewRoomsLabel}
            </Button>
          </span>
        </header>
        {/* A frase do agente (✦), entre a identidade e as comodidades — régua do
            protótipo (2026-08-27). Só existe quando a oferta a traz. O glifo é o
            caractere ✦ mesmo (o Sparkle do Lucide vira um "+" gordo neste corpo). */}
        {hotel.summary && (
          <p className="hui-hotel-page-summary">
            <span aria-hidden="true">✦</span>
            {hotel.summary}
          </p>
        )}
        <HotelDetailsSections
          hotel={hotel}
          copy={copy}
          rooms={rooms}
          otherRooms={otherRooms}
          roomsCopy={roomsCopy}
          selectedRateId={selectedRateId}
          initialFilters={initialFilters}
          readOnly={readOnly}
          busy={busy}
          stayArea={stayArea}
          roomsPhase={roomsPhase}
          onRateSelect={onRateSelect}
          onRoomDetailsOpen={onRoomDetailsOpen}
        />
      </div>
    </div>
  );
}

/** O gesto de fechar da folha do celular: o ✕ da barra, o Escape e o véu do
 *  Drawer caem todos no mesmo `onClose` do host (que limpa `?hotel=` e reexibe a
 *  lista embaixo). */
function HotelDetailsSheetFrame({
  label,
  busy = false,
  onClose,
  children,
}: {
  readonly label: string;
  readonly busy?: boolean;
  readonly onClose: () => void;
  readonly children: ReactNode;
}) {
  // A página não tem gatilho próprio (quem a abre é o host, pela URL ou pelo
  // card da lista), então não há pra onde devolver o foco: o ref vazio deixa o
  // Base UI sem alvo e o foco fica onde o host o puser ao remontar a lista.
  const noReturnFocus = useRef<HTMLElement>(null);
  return (
    <BottomSheet
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      returnFocusTo={noReturnFocus}
      label={label}
      fullscreen
    >
      <section
        className="genui2 hui-hotel-details hui-hotel-sheet"
        aria-label={label}
        aria-busy={busy || undefined}
      >
        {children}
      </section>
    </BottomSheet>
  );
}

/** A barra fixa da folha: ✕ à esquerda, o coração à direita e, no meio, o ECO
 *  do nome do hotel — invisível enquanto o título grande do corpo está na tela
 *  e aceso (`data-condensed`) quando ele rola pra fora, o "se ajusta conforme o
 *  scroll" da issue #2668. O eco é decorativo (`aria-hidden`): o `<h2>` de
 *  verdade mora no corpo e o diálogo já leva o nome como rótulo. */
function HotelDetailsSheetBar({
  closeLabel,
  title,
  condensed = false,
  favorite,
  openInNewTab,
  busy = false,
  onClose,
}: {
  readonly closeLabel: string;
  readonly title?: string;
  readonly condensed?: boolean;
  readonly favorite?: HotelDetailsFavorite;
  readonly openInNewTab?: HotelDetailsOpenInNewTab;
  readonly busy?: boolean;
  readonly onClose: () => void;
}) {
  return (
    <header className="hui-hotel-sheet-bar" data-condensed={condensed}>
      <HotelDetailsCloseButton label={closeLabel} onClose={onClose} />
      {title && (
        <span className="hui-hotel-sheet-bar-title" aria-hidden="true">
          {title}
        </span>
      )}
      <span className="hui-hotel-details-actions">
        {favorite && <HotelDetailsFavoriteButton favorite={favorite} disabled={busy} />}
        {openInNewTab && <HotelDetailsNewTabButton openInNewTab={openInNewTab} />}
      </span>
    </header>
  );
}

/**
 * A Página do Hotel no palco ESTREITO (#2668): a MESMA folha de tela cheia da
 * busca e dos filtros (`BottomSheet fullscreen`), por cima da lista. Sem trilha
 * nem breadcrumb (no celular ninguém foi pra outra aba — o ✕ volta pra tela de
 * baixo); o nome do hotel em destaque acima das fotos, ecoado na barra quando
 * rola pra fora; a grade da GAVETA na galeria (o mosaico 2×2 + grande da página
 * larga viraria selos numa tela de 390px) e as abas/scrollspy da gaveta como
 * navegação. As seções são as mesmas da página (com a estadia dentro dos
 * quartos, `stayArea`/`roomsPhase`).
 */
function HotelDetailsSheetReady({
  copy,
  roomsCopy,
  rooms,
  otherRooms,
  selectedRateId,
  initialFilters,
  readOnly = false,
  busy = false,
  favorite,
  openInNewTab,
  stayArea,
  roomsPhase,
  onClose,
  onRateSelect,
  onRoomDetailsOpen,
  hotel,
}: Omit<HotelDetailsContentProps, 'onRetry'> & { readonly hotel: HotelDetailsView }) {
  const tabs = useMemo(() => detailTabs({ hotel, copy }), [hotel, copy]);
  const { bodyRef, visibleTab, activateSection, trackSection } = useHotelDetailsScrollspy(tabs);
  const identityRef = useRef<HTMLDivElement>(null);
  const [condensed, setCondensed] = useState(false);
  // Em RETÂNGULOS, não em `offsetTop`: o corpo não é posicionado, então o
  // `offsetParent` do bloco é o popup fixo do Drawer e a conta sairia deslocada
  // pela barra + safe-area. Condensa quando o bloco do título saiu inteiro por
  // cima da área visível do corpo.
  const trackIdentity = useCallback((body: HTMLDivElement) => {
    const identity = identityRef.current;
    if (!identity) return;
    setCondensed(identity.getBoundingClientRect().bottom <= body.getBoundingClientRect().top);
  }, []);

  return (
    <HotelDetailsSheetFrame label={hotel.name} busy={busy} onClose={onClose}>
      <HotelDetailsSheetBar
        closeLabel={copy.closeLabel}
        title={hotel.name}
        condensed={condensed}
        {...(favorite ? { favorite } : {})}
        openInNewTab={openInNewTab}
        busy={busy}
        onClose={onClose}
      />
      <div
        ref={bodyRef}
        className="hui-hotel-details-body scrollbar-rail"
        onScroll={(event) => {
          trackSection(event.currentTarget);
          trackIdentity(event.currentTarget);
        }}
      >
        <div ref={identityRef} className="hui-hotel-sheet-identity">
          <h2>{hotel.name}</h2>
          {hotel.rating && <HotelDetailsPageRating rating={hotel.rating} />}
        </div>
        <HotelDetailsGallery
          photos={hotel.photos}
          title={hotel.name}
          copy={{
            closeLabel: copy.closeLabel,
            previousPhotoLabel: copy.previousPhotoLabel,
            nextPhotoLabel: copy.nextPhotoLabel,
          }}
        />
        <HotelDetailsTabs
          tabs={tabs}
          activeId={visibleTab}
          copy={copy}
          onActiveChange={activateSection}
        />
        {/* A frase do agente (✦) é peça da PÁGINA, e a folha é a página: fica
            logo abaixo das abas, abrindo o corpo antes das comodidades. */}
        {hotel.summary && (
          <p className="hui-hotel-page-summary">
            <span aria-hidden="true">✦</span>
            {hotel.summary}
          </p>
        )}
        <HotelDetailsSections
          hotel={hotel}
          copy={copy}
          rooms={rooms}
          otherRooms={otherRooms}
          roomsCopy={roomsCopy}
          selectedRateId={selectedRateId}
          initialFilters={initialFilters}
          readOnly={readOnly}
          busy={busy}
          stayArea={stayArea}
          roomsPhase={roomsPhase}
          onRateSelect={onRateSelect}
          onRoomDetailsOpen={onRoomDetailsOpen}
        />
      </div>
    </HotelDetailsSheetFrame>
  );
}

/** Os 3 estados na moldura de FOLHA (celular): loading e error ganham a mesma
 *  barra com ✕ — nunca a trilha da página larga nem o cabeçalho da gaveta. */
function HotelDetailsSheet(
  props: HotelDetailsContentProps & {
    readonly state: 'loading' | 'ready' | 'error';
    readonly message?: string;
  },
) {
  const { state, copy, favorite, openInNewTab, onClose } = props;
  if (state === 'loading') {
    return (
      <HotelDetailsSheetFrame label={copy.loadingLabel} busy onClose={onClose}>
        <HotelDetailsSheetBar
          closeLabel={copy.closeLabel}
          {...(favorite ? { favorite } : {})}
          openInNewTab={openInNewTab}
          onClose={onClose}
        />
        <div className="hui-hotel-details-body scrollbar-rail">
          <output className="hui-hotel-details-visually-hidden">{copy.loadingLabel}</output>
          <div className="hui-hotel-sheet-identity" aria-hidden="true">
            <Skeleton className="hui-hotel-details-skeleton-name" />
            <Skeleton className="hui-hotel-details-skeleton-rating" />
          </div>
          <HotelDetailsSkeletonBody />
        </div>
      </HotelDetailsSheetFrame>
    );
  }
  if (state === 'error' || !props.hotel) {
    return (
      <HotelDetailsSheetFrame label={copy.errorTitleLabel} onClose={onClose}>
        <HotelDetailsSheetBar
          closeLabel={copy.closeLabel}
          {...(favorite ? { favorite } : {})}
          openInNewTab={openInNewTab}
          onClose={onClose}
        />
        <div className="hui-hotel-details-body scrollbar-rail" data-centered="true">
          <HotelDetailsFailureAlert
            copy={copy}
            {...(props.message ? { message: props.message } : {})}
            {...(props.onRetry ? { onRetry: props.onRetry } : {})}
          />
        </div>
      </HotelDetailsSheetFrame>
    );
  }
  return <HotelDetailsSheetReady {...props} hotel={props.hotel} />;
}

/**
 * Orquestra os 3 estados na moldura de PÁGINA. loading e error ganham o layout
 * de página — NUNCA o ✕/cabeçalho da gaveta (o bug que o painel tinha era cair
 * no skeleton/failure da gaveta antes de chegar aqui). A volta é do host. Só é
 * alcançado com `pageCopy` presente (o switch garante).
 */
function HotelDetailsPage(
  props: HotelDetailsContentProps & {
    readonly state: 'loading' | 'ready' | 'error';
    readonly message?: string;
    readonly pageCopy: HotelDetailsPageCopy;
  },
) {
  const { state, copy, pageCopy } = props;
  if (state === 'loading') {
    return (
      <div className={HOTEL_PAGE_CLASS} aria-busy aria-label={copy.loadingLabel}>
        <output className="hui-hotel-details-visually-hidden">{copy.loadingLabel}</output>
        <HotelDetailsSkeletonGallery />
        <div className="hui-hotel-page-column" aria-hidden="true">
          <HotelDetailsSkeletonAmenities />
          <HotelDetailsSkeletonRooms />
        </div>
      </div>
    );
  }
  if (state === 'error' || !props.hotel) {
    return (
      <div className={HOTEL_PAGE_CLASS} aria-label={copy.errorTitleLabel}>
        <div className="hui-hotel-page-column">
          <HotelDetailsFailureAlert
            copy={copy}
            {...(props.message ? { message: props.message } : {})}
            {...(props.onRetry ? { onRetry: props.onRetry } : {})}
          />
        </div>
      </div>
    );
  }
  return <HotelDetailsPageReady {...props} hotel={props.hotel} pageCopy={pageCopy} />;
}

/** Espelha a geometria do estado pronto para a transição não deslocar a casca. */
function HotelDetailsSkeleton({
  copy,
  onClose,
}: {
  readonly copy: HotelDetailsCopy;
  readonly onClose: () => void;
}) {
  return (
    <section className="genui2 hui-hotel-details" aria-busy aria-label={copy.loadingLabel}>
      <header className="hui-hotel-details-header">
        <div className="hui-hotel-details-identity">
          <Skeleton className="hui-hotel-details-skeleton-name" />
          <Skeleton className="hui-hotel-details-skeleton-rating" />
        </div>
        <HotelDetailsCloseButton label={copy.closeLabel} onClose={onClose} />
      </header>
      <div className="hui-hotel-details-body scrollbar-rail">
        {/* O rótulo de carregamento existe só para leitor de tela: na tela quem
         *  comunica a espera são as barras. */}
        <output className="hui-hotel-details-visually-hidden">{copy.loadingLabel}</output>
        <HotelDetailsSkeletonBody />
      </div>
    </section>
  );
}

/** O alerta de falha, um só pras três molduras (gaveta, página e folha). */
function HotelDetailsFailureAlert({
  copy,
  message,
  onRetry,
}: {
  readonly copy: HotelDetailsCopy;
  readonly message?: string;
  readonly onRetry?: () => void;
}) {
  return (
    <div className="hui-hotel-details-failure" role="alert">
      <span className="hui-hotel-details-failure-icon" aria-hidden="true">
        <TriangleAlertIcon />
      </span>
      <h2>{copy.errorTitleLabel}</h2>
      {message && <p>{message}</p>}
      {onRetry && (
        <Button size="sm" onClick={onRetry}>
          {copy.retryLabel}
        </Button>
      )}
    </div>
  );
}

function HotelDetailsFailure({
  copy,
  message,
  onClose,
  onRetry,
}: {
  readonly copy: HotelDetailsCopy;
  readonly message?: string;
  readonly onClose: () => void;
  readonly onRetry?: () => void;
}) {
  return (
    <section className="genui2 hui-hotel-details" aria-label={copy.errorTitleLabel}>
      <header className="hui-hotel-details-header" data-bare="true">
        <HotelDetailsCloseButton label={copy.closeLabel} onClose={onClose} />
      </header>
      <div className="hui-hotel-details-body scrollbar-rail" data-centered="true">
        <HotelDetailsFailureAlert
          copy={copy}
          {...(message ? { message } : {})}
          {...(onRetry ? { onRetry } : {})}
        />
      </div>
    </section>
  );
}

export function HotelDetailsPanel(props: Readonly<HotelDetailsPanelProps>) {
  // A MESMA régua da barra, do resultado e do mapa cheio (`HOTEL_NARROW_QUERY`).
  const narrow = useMediaQuery(HOTEL_NARROW_QUERY);
  // A variante PÁGINA trata os próprios loading/error/ready na moldura de página
  // (trilha de volta, sem o ✕ da gaveta), então roteia ANTES do fluxo da gaveta.
  // No palco estreito a página é a FOLHA de tela cheia (#2668) — só ela lê a
  // largura: a gaveta já é a casca do host, e ele decide quando montá-la.
  if (props.variant === 'page' && props.pageCopy) {
    if (narrow) return <HotelDetailsSheet {...props} />;
    return <HotelDetailsPage {...props} pageCopy={props.pageCopy} />;
  }
  if (props.state === 'loading')
    return <HotelDetailsSkeleton copy={props.copy} onClose={props.onClose} />;
  if (props.state === 'error' || !props.hotel)
    return (
      <HotelDetailsFailure
        copy={props.copy}
        {...(props.message ? { message: props.message } : {})}
        onClose={props.onClose}
        {...(props.onRetry ? { onRetry: props.onRetry } : {})}
      />
    );
  return <HotelDetailsReady {...props} hotel={props.hotel} />;
}
